/**
 * Country & Region Data
 *
 * Provides country list and conditional province/state lists
 * for the guest estimate submission form.
 */

export interface RegionOption {
  /** Display name, in the active language. */
  value: string;
  /** The English name — what forms submit and estimates.region stores. */
  valueEn: string;
  code: string;
}

export interface CountryOption {
  value: string;
  code: string;
  /** What this country calls its first-level subdivisions (label shown on the
   *  region field). Defaults to 'Region' via getRegionLabel() when omitted. */
  regionLabel?: string;
}

export const COUNTRIES: CountryOption[] = [
  { value: 'Canada', code: 'CA', regionLabel: 'Province' },
  { value: 'United States', code: 'US', regionLabel: 'State' },
  { value: 'Cayman Islands', code: 'KY', regionLabel: 'District' },
];

export const PROVINCES_CA: RegionOption[] = [
  { value: 'Alberta', valueEn: 'Alberta', code: 'CA-AB' },
  { value: 'British Columbia', valueEn: 'British Columbia', code: 'CA-BC' },
  { value: 'Manitoba', valueEn: 'Manitoba', code: 'CA-MB' },
  { value: 'New Brunswick', valueEn: 'New Brunswick', code: 'CA-NB' },
  { value: 'Newfoundland and Labrador', valueEn: 'Newfoundland and Labrador', code: 'CA-NL' },
  { value: 'Northwest Territories', valueEn: 'Northwest Territories', code: 'CA-NT' },
  { value: 'Nova Scotia', valueEn: 'Nova Scotia', code: 'CA-NS' },
  { value: 'Nunavut', valueEn: 'Nunavut', code: 'CA-NU' },
  { value: 'Ontario', valueEn: 'Ontario', code: 'CA-ON' },
  { value: 'Prince Edward Island', valueEn: 'Prince Edward Island', code: 'CA-PE' },
  { value: 'Quebec', valueEn: 'Quebec', code: 'CA-QC' },
  { value: 'Saskatchewan', valueEn: 'Saskatchewan', code: 'CA-SK' },
  { value: 'Yukon Territories', valueEn: 'Yukon Territories', code: 'CA-YT' },
];

export const STATES_US: RegionOption[] = [
  { value: 'Alabama', valueEn: 'Alabama', code: 'US-AL' },
  { value: 'Alaska', valueEn: 'Alaska', code: 'US-AK' },
  { value: 'Arizona', valueEn: 'Arizona', code: 'US-AZ' },
  { value: 'Arkansas', valueEn: 'Arkansas', code: 'US-AR' },
  { value: 'California', valueEn: 'California', code: 'US-CA' },
  { value: 'Colorado', valueEn: 'Colorado', code: 'US-CO' },
  { value: 'Connecticut', valueEn: 'Connecticut', code: 'US-CT' },
  { value: 'Delaware', valueEn: 'Delaware', code: 'US-DE' },
  { value: 'District of Columbia', valueEn: 'District of Columbia', code: 'US-DC' },
  { value: 'Florida', valueEn: 'Florida', code: 'US-FL' },
  { value: 'Georgia', valueEn: 'Georgia', code: 'US-GA' },
  { value: 'Hawaii', valueEn: 'Hawaii', code: 'US-HI' },
  { value: 'Idaho', valueEn: 'Idaho', code: 'US-ID' },
  { value: 'Illinois', valueEn: 'Illinois', code: 'US-IL' },
  { value: 'Indiana', valueEn: 'Indiana', code: 'US-IN' },
  { value: 'Iowa', valueEn: 'Iowa', code: 'US-IA' },
  { value: 'Kansas', valueEn: 'Kansas', code: 'US-KS' },
  { value: 'Kentucky', valueEn: 'Kentucky', code: 'US-KY' },
  { value: 'Louisiana', valueEn: 'Louisiana', code: 'US-LA' },
  { value: 'Maine', valueEn: 'Maine', code: 'US-ME' },
  { value: 'Maryland', valueEn: 'Maryland', code: 'US-MD' },
  { value: 'Massachusetts', valueEn: 'Massachusetts', code: 'US-MA' },
  { value: 'Michigan', valueEn: 'Michigan', code: 'US-MI' },
  { value: 'Minnesota', valueEn: 'Minnesota', code: 'US-MN' },
  { value: 'Mississippi', valueEn: 'Mississippi', code: 'US-MS' },
  { value: 'Missouri', valueEn: 'Missouri', code: 'US-MO' },
  { value: 'Montana', valueEn: 'Montana', code: 'US-MT' },
  { value: 'Nebraska', valueEn: 'Nebraska', code: 'US-NE' },
  { value: 'Nevada', valueEn: 'Nevada', code: 'US-NV' },
  { value: 'New Hampshire', valueEn: 'New Hampshire', code: 'US-NH' },
  { value: 'New Jersey', valueEn: 'New Jersey', code: 'US-NJ' },
  { value: 'New Mexico', valueEn: 'New Mexico', code: 'US-NM' },
  { value: 'New York', valueEn: 'New York', code: 'US-NY' },
  { value: 'North Carolina', valueEn: 'North Carolina', code: 'US-NC' },
  { value: 'North Dakota', valueEn: 'North Dakota', code: 'US-ND' },
  { value: 'Ohio', valueEn: 'Ohio', code: 'US-OH' },
  { value: 'Oklahoma', valueEn: 'Oklahoma', code: 'US-OK' },
  { value: 'Oregon', valueEn: 'Oregon', code: 'US-OR' },
  { value: 'Pennsylvania', valueEn: 'Pennsylvania', code: 'US-PA' },
  { value: 'Rhode Island', valueEn: 'Rhode Island', code: 'US-RI' },
  { value: 'South Carolina', valueEn: 'South Carolina', code: 'US-SC' },
  { value: 'South Dakota', valueEn: 'South Dakota', code: 'US-SD' },
  { value: 'Tennessee', valueEn: 'Tennessee', code: 'US-TN' },
  { value: 'Texas', valueEn: 'Texas', code: 'US-TX' },
  { value: 'Utah', valueEn: 'Utah', code: 'US-UT' },
  { value: 'Vermont', valueEn: 'Vermont', code: 'US-VT' },
  { value: 'Virginia', valueEn: 'Virginia', code: 'US-VA' },
  { value: 'Washington', valueEn: 'Washington', code: 'US-WA' },
  { value: 'West Virginia', valueEn: 'West Virginia', code: 'US-WV' },
  { value: 'Wisconsin', valueEn: 'Wisconsin', code: 'US-WI' },
  { value: 'Wyoming', valueEn: 'Wyoming', code: 'US-WY' },
];

/**
 * Districts of the Cayman Islands (the 6 districts of Grand Cayman + the two
 * Sister Islands). The Cayman Islands has no official ISO 3166-2 subdivisions,
 * so the `code` values are an internal KY-* convention (used only as option
 * keys); the `value` is what is submitted to Pardot/Salesforce — keep it
 * proper-cased to match their picklist/text expectations.
 */
export const DISTRICTS_KY: RegionOption[] = [
  { value: 'George Town', valueEn: 'George Town', code: 'KY-GT' },
  { value: 'West Bay', valueEn: 'West Bay', code: 'KY-WB' },
  { value: 'Bodden Town', valueEn: 'Bodden Town', code: 'KY-BT' },
  { value: 'North Side', valueEn: 'North Side', code: 'KY-NS' },
  { value: 'East End', valueEn: 'East End', code: 'KY-EE' },
  { value: 'Cayman Brac', valueEn: 'Cayman Brac', code: 'KY-CB' },
  { value: 'Little Cayman', valueEn: 'Little Cayman', code: 'KY-LC' },
];

/**
 * Get the region list for a given country.
 */
export function getRegionsForCountry(country: string): RegionOption[] {
  switch (country) {
    case 'Canada':
      return PROVINCES_CA;
    case 'United States':
      return STATES_US;
    case 'Cayman Islands':
      return DISTRICTS_KY;
    default:
      return [];
  }
}

/**
 * The label to show on the region field for a country (e.g. "State",
 * "Province", "District"). Falls back to the global-neutral "Region" for
 * countries without a specific term or before a country is chosen.
 */
export function getRegionLabel(country: string): string {
  return COUNTRIES.find((c) => c.value === country)?.regionLabel ?? 'Region';
}

// ============================================================================
// Admin-managed geo (hydrated from /api/geo.php). The hardcoded lists above are
// the offline fallback; fallbackGeo() shapes them like the API response so the
// form can treat both identically.
// ============================================================================

export interface GeoCountry {
  /** Display name, in the active language. */
  name: string;
  /**
   * The English name. This is what forms SUBMIT and what estimates.country
   * stores — never the translated one, or the same country files itself under
   * a different string per visitor language and stops being queryable as one.
   */
  nameEn: string;
  isoCode: string;
  regionLabel: string;
  regions: RegionOption[];
}

/** Build the fallback geo list from the hardcoded constants in this file. */
export function fallbackGeo(): GeoCountry[] {
  return COUNTRIES.map((c) => ({
    name: c.value,
    // The offline fallback is English, so the two coincide here.
    nameEn: c.value,
    isoCode: c.code,
    regionLabel: c.regionLabel ?? 'Region',
    regions: getRegionsForCountry(c.value),
  }));
}

/** Region label for a country within a hydrated geo list. */
export function regionLabelFor(geo: GeoCountry[], country: string): string {
  // Matches on nameEn, because `country` here is a STORED value (what the
  // select submitted), not a displayed one. Matching on `name` would find
  // nothing the moment the visitor is not in English — the region dropdown
  // would silently come back empty.
  return geo.find((c) => c.nameEn === country || c.name === country)?.regionLabel ?? 'Region';
}

/** Region options for a country within a hydrated geo list. */
export function regionsFor(geo: GeoCountry[], country: string): RegionOption[] {
  // Same as regionLabelFor: `country` is the stored English name. The `name`
  // fallback covers profiles saved before this split, which hold whatever the
  // display name was at the time.
  return geo.find((c) => c.nameEn === country || c.name === country)?.regions ?? [];
}

/**
 * User category options for the "I am a" dropdown.
 */
export const USER_CATEGORIES = [
  'Architect / Designer',
  'Building Materials Supplier',
  'Contractor',
  'Professional Home Builder',
  'Homeowner',
  'Business Owner',
];
